import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { InvoiceTableRow } from './InvoiceTableRow';
import { Invoice, User, InvoiceStatus } from '../../types';

type SortField = 'month' | 'employee' | 'amount' | 'uploaded';
type SortDirection = 'asc' | 'desc';

const DEFAULT_DIRECTIONS: Record<SortField, SortDirection> = {
  month: 'desc',
  employee: 'asc',
  amount: 'desc',
  uploaded: 'desc',
};

interface InvoiceTableProps {
  invoices: Invoice[];
  loading: boolean;
  userRole?: string;
  currentUserId?: string;
  users?: User[];
  selectedInvoiceIds: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectInvoice: (id: string, checked: boolean) => void;
  onUpdateInvoice: (invoice: Invoice) => void;
  onDeleteInvoice: (id: string) => void;
  onViewHistory: (invoice: Invoice) => void;
  onStatusChange?: (invoiceId: string, newStatus: InvoiceStatus) => void;
}

interface InvoiceTotals {
  amountTotal: number;
  rateTotal: number;
}

const calculateTotals = (invoices: Invoice[], users: User[]): InvoiceTotals => {
  let amountTotal = 0;
  let rateTotal = 0;

  for (const invoice of invoices) {
    const amount = invoice.overrideTotal ?? invoice.extractedInvoiceTotal;
    if (amount != null) {
      amountTotal += amount;
    }

    const invoiceUser = users.find(u => u.id === invoice.userId);
    if (invoiceUser?.contractRate) {
      rateTotal += invoiceUser.contractRate;
    }
  }

  return { amountTotal, rateTotal };
};

export const InvoiceTable: React.FC<InvoiceTableProps> = ({
  invoices,
  loading,
  userRole,
  currentUserId,
  users = [],
  selectedInvoiceIds,
  onSelectAll,
  onSelectInvoice,
  onUpdateInvoice,
  onDeleteInvoice,
  onViewHistory,
  onStatusChange,
}) => {
  const { t } = useTranslation('invoices');
  const allSelected = invoices.length > 0 && selectedInvoiceIds.length === invoices.length;
  // Contract rates are shown to everyone who reviews invoices: admins see the whole org and
  // managers see their reporting chain, which is exactly the set of invoices the API returns them.
  const showRateColumn = userRole === 'admin' || userRole === 'manager';

  const totals = useMemo(() => {
    const invoicesToSum = selectedInvoiceIds.length > 0
      ? invoices.filter(inv => selectedInvoiceIds.includes(inv.id))
      : invoices;
    return calculateTotals(invoicesToSum, users);
  }, [invoices, users, selectedInvoiceIds]);

  const [sortConfig, setSortConfig] = useState<{ field: SortField | null; direction: SortDirection }>({
    field: null,
    direction: 'desc',
  });

  const handleSort = useCallback((field: SortField) => {
    setSortConfig(prev => {
      if (prev.field === field) {
        return { ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field, direction: DEFAULT_DIRECTIONS[field] };
    });
  }, []);

  const sortField = sortConfig.field;
  const sortDirection = sortConfig.direction;

  const sortedInvoices = useMemo(() => {
    const copy = [...invoices];
    if (sortField === null) {
      return copy.sort((a, b) => b.invoiceMonth.localeCompare(a.invoiceMonth));
    }

    const dir = sortDirection === 'asc' ? 1 : -1;

    return copy.sort((a, b) => {
      switch (sortField) {
        case 'month':
          return dir * a.invoiceMonth.localeCompare(b.invoiceMonth);
        case 'employee': {
          const nameA = (a.userName ?? '').toLowerCase();
          const nameB = (b.userName ?? '').toLowerCase();
          if (!nameA && !nameB) return 0;
          if (!nameA) return 1;
          if (!nameB) return -1;
          return dir * nameA.localeCompare(nameB);
        }
        case 'amount': {
          const amtA = a.overrideTotal ?? a.extractedInvoiceTotal;
          const amtB = b.overrideTotal ?? b.extractedInvoiceTotal;
          if (amtA == null && amtB == null) return 0;
          if (amtA == null) return 1;
          if (amtB == null) return -1;
          return dir * (amtA - amtB);
        }
        case 'uploaded':
          return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        default:
          return 0;
      }
    });
  }, [invoices, sortField, sortDirection]);

  return (
    <div className="rounded-md border overflow-x-auto" data-testid="invoice-table-container">
      <Table data-testid="invoice-table">
        <TableHeader>
          <TableRow>
            {(userRole === 'admin' || userRole === 'manager') && (
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={onSelectAll}
                  aria-label="Select all"
                  data-testid="invoice-table-select-all"
                />
              </TableHead>
            )}
            <TableHead className="w-[100px]">{t('table.headers.month')}</TableHead>
            <TableHead className="w-[140px]">
              <button
                type="button"
                className="inline-flex items-center gap-1 hover:text-foreground"
                onClick={() => handleSort('employee')}
                aria-label={t('table.sortBy', { column: t('table.headers.employee') })}
                data-testid="invoice-table-sort-employee"
              >
                {t('table.headers.employee')}
                {sortField === 'employee' && (sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
              </button>
            </TableHead>
            <TableHead className="w-[140px]">{t('table.headers.vendor')}</TableHead>
            <TableHead className="w-[110px]">
              <button
                type="button"
                className="inline-flex items-center gap-1 hover:text-foreground"
                onClick={() => handleSort('amount')}
                aria-label={t('table.sortBy', { column: t('table.headers.amount') })}
                data-testid="invoice-table-sort-amount"
              >
                {t('table.headers.amount')}
                {sortField === 'amount' && (sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
              </button>
            </TableHead>
            {showRateColumn && <TableHead className="w-[90px]">{t('table.headers.rate')}</TableHead>}
            <TableHead className="max-w-[200px]">{t('table.headers.fileName')}</TableHead>
            <TableHead className="w-[120px]">{t('table.headers.status')}</TableHead>
            <TableHead className="w-[100px]">
              <button
                type="button"
                className="inline-flex items-center gap-1 hover:text-foreground"
                onClick={() => handleSort('uploaded')}
                aria-label={t('table.sortBy', { column: t('table.headers.uploaded') })}
                data-testid="invoice-table-sort-uploaded"
              >
                {t('table.headers.uploaded')}
                {sortField === 'uploaded' && (sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
              </button>
            </TableHead>
            <TableHead className="w-[80px] text-right">{t('table.headers.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow data-testid="invoice-table-loading">
              <TableCell colSpan={9} className="text-center h-32">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              </TableCell>
            </TableRow>
          ) : invoices.length === 0 ? (
            <TableRow data-testid="invoice-table-empty">
              <TableCell colSpan={9} className="text-center h-32 text-muted-foreground">
                {t('table.noInvoices')}
              </TableCell>
            </TableRow>
          ) : (
            sortedInvoices.map((invoice) => (
                            <InvoiceTableRow
                              key={invoice.id}
                              invoice={invoice}
                              userRole={userRole === 'manager' && invoice.userId === currentUserId ? 'user' : userRole}
                              showSelectionColumn={userRole === 'admin' || userRole === 'manager'}
                              showRateColumn={showRateColumn}
                              users={users}
                              isSelected={selectedInvoiceIds.includes(invoice.id)}
                              onSelect={(checked) => onSelectInvoice(invoice.id, checked)}
                              onUpdate={onUpdateInvoice}
                              onDelete={onDeleteInvoice}
                              onViewHistory={onViewHistory}
                              onStatusChange={onStatusChange}
                            />
            ))
          )}
        </TableBody>
        {!loading && invoices.length > 0 && (
          <TableFooter data-testid="invoice-table-footer">
            <TableRow>
              {(userRole === 'admin' || userRole === 'manager') && <TableCell />}
              <TableCell className="font-bold">
                {selectedInvoiceIds.length > 0
                  ? t('table.totalSelected', { count: selectedInvoiceIds.length })
                  : t('table.total')}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell className="font-bold">
                ${totals.amountTotal.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TableCell>
                            {showRateColumn && (
                              <TableCell className="font-bold">
                  ${totals.rateTotal.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </TableCell>
              )}
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </div>
  );
};
